import type { AddOnKey, ListAddOnsResponse } from '@air/api';
import { useCallback, useMemo } from 'react';

import { type FeatureFlags, useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useCurrentWorkspaceAddOns } from '~/swr-hooks/workspaces/addOns/useCurrentWorkspaceAddOns';
import { isCypressRun } from '~/utils/PathUtils';

type AddOn = ListAddOnsResponse['data'][number];
export type SupportedAddOnKey = Extract<AddOnKey, 'libraries'>;
export type AddOnFlagValue = boolean | undefined;

export const usePrivateAddOnFeatures = () => {
  const { data: addOns, isPending } = useCurrentWorkspaceAddOns({ enabled: true });
  const isReady = useMemo(() => !isPending && !!addOns, [isPending, addOns]);

  const addOnsMap = useMemo(
    (): Map<AddOnKey, AddOn> => (addOns?.data ? new Map(addOns.data.map((addOn) => [addOn.key, addOn])) : new Map()),
    [addOns?.data],
  );

  const featureFlags = useFeatureFlags();
  const getFeatureFlagValue = useCallback(
    (feature: AddOnKey): AddOnFlagValue => {
      const featureFlagValue = featureFlags[feature as keyof FeatureFlags];

      return typeof featureFlagValue === 'boolean' ? featureFlagValue : undefined;
    },
    [featureFlags],
  );

  const isLibrariesEnabled = useCallback(() => {
    if (!isReady) {
      return undefined;
    }

    // TODO: after we've retired the libraries flag, the remaining logic
    // in this function can be replaced with:
    // const featureInAddOns = addOnsMap.get('libraries');
    // if (featureInAddOns) {
    //   return featureInAddOns.enabled === true && featureInAddOns.visible === true;
    // }

    // return false;

    const featureInAddOns = addOnsMap.get('libraries');
    // NOTE: enabling Libraries in add-ons is a one-way operation
    if (featureInAddOns && featureInAddOns.enabled === true && featureInAddOns.visible === true) {
      return true;
    }

    const flagValue = getFeatureFlagValue('libraries');

    return flagValue === undefined && isCypressRun() ? false : flagValue;
  }, [isReady, addOnsMap, getFeatureFlagValue]);

  const addOnFeatures = useMemo(
    (): Record<SupportedAddOnKey, AddOnFlagValue> => ({
      libraries: isLibrariesEnabled(),
    }),
    [isLibrariesEnabled],
  );

  return addOnFeatures;
};
